// AIMS PWA - "Take offline" dialog (AIMS-Offline-Inspection.md §8 controls).
//
// Three-stage flow:
//  1. WARN  - blocking data-loss acknowledgment. The user must tick a required
//     checkbox and press Confirm (NOT a one-tap dismiss). Copy states that
//     offline inspections live only on this device until synced, so logging out,
//     uninstalling/removing the PWA, clearing browser data, or losing the device
//     before a successful sync will PERMANENTLY lose that work. Also shows the
//     storage-budget estimate. (Locked decision, Ben 2026-07-07.)
//  2. WORK  - runs takeVisitOffline with live progress.
//  3. DONE  - atomic "everything cached ✓" confirmation, or an error with Retry.

import React, { useEffect, useState, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import LinearProgress from '@mui/material/LinearProgress';
import Alert from '@mui/material/Alert';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import { takeVisitOffline, getStorageEstimate, isVisitOffline, releaseVisitOffline } from '../../offline/takeOffline';
import { isStandalone } from '../../shared';

const PHASE_LABEL = {
  checkout: 'Reserving assets…',
  templates: 'Downloading checklists…',
  typeMap: 'Downloading settings…',
  caching: 'Saving to device…',
  verify: 'Verifying everything saved…',
};

const fmtMB = (bytes) => (bytes == null ? null : (bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0) + ' MB');

export default function TakeOfflineDialog({ open, onClose, visit, onDone }) {
  const [stage, setStage] = useState('warn'); // manage | warn | work | done
  const [ack, setAck] = useState(false);
  const [progress, setProgress] = useState(null); // { phase, done, total }
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [storage, setStorage] = useState({ usage: null, quota: null });
  const [removing, setRemoving] = useState(false);
  const [removeBlocked, setRemoveBlocked] = useState(0); // pending count blocking removal

  const assetCount = visit && Array.isArray(visit.assets) ? visit.assets.length : (visit && visit.asset_count) || 0;

  useEffect(() => {
    if (!open || !visit) return;
    setAck(false); setProgress(null); setError(null); setResult(null); setRemoving(false); setRemoveBlocked(0);
    getStorageEstimate().then(setStorage);
    // If already cached offline, open the manage view (sync/remove) instead of
    // re-running the download+warning flow.
    isVisitOffline(visit.id).then((cached) => setStage(cached ? 'manage' : 'warn'));
  }, [open, visit]);

  const remove = useCallback(async (force) => {
    setRemoving(true); setError(null);
    try {
      const res = await releaseVisitOffline(visit.id, { force });
      if (res.blocked) { setRemoveBlocked(res.pending); setRemoving(false); return; }
      setRemoving(false);
      if (onDone) onDone({ removed: true });
      onClose();
    } catch (e) {
      setError(e && e.message ? e.message : 'Could not remove the offline copy.');
      setRemoving(false);
    }
  }, [visit, onDone, onClose]);

  const run = useCallback(async () => {
    setStage('work'); setError(null); setProgress({ phase: 'checkout', done: 0, total: 1 });
    try {
      const r = await takeVisitOffline(visit.id, {
        deviceLabel: navigator.userAgent ? navigator.userAgent.slice(0, 110) : null,
        onProgress: setProgress,
      });
      setResult(r);
      setStage('done');
      if (onDone) onDone(r);
    } catch (e) {
      setError(e && e.message ? e.message : 'Something went wrong taking this visit offline.');
      setStage('done');
    }
  }, [visit, onDone]);

  const free = storage.quota != null && storage.usage != null ? storage.quota - storage.usage : null;

  return (
    <Dialog open={open} onClose={stage === 'work' ? undefined : onClose} fullWidth maxWidth="xs"
      PaperProps={{ sx: { background: 'var(--surface)', borderRadius: 'var(--r, 12px)' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 17, fontWeight: 700 }}>
        <CloudOffIcon fontSize="small" sx={{ color: 'var(--primary)' }} />
        {stage === 'manage' ? 'Offline visit' : 'Take visit offline'}
      </DialogTitle>

      <DialogContent dividers>
        {stage === 'manage' && (
          <Box>
            <Box sx={{ fontSize: 13.5, color: 'var(--text)', mb: 1.5 }}>
              This visit is saved on your device{visit && visit.company ? <> for <b>{visit.company}</b></> : null} and ready to inspect offline.
            </Box>
            {removeBlocked > 0 ? (
              <Alert severity="warning" sx={{ '& .MuiAlert-message': { fontSize: 12.5 } }}>
                <b>{removeBlocked} inspection{removeBlocked === 1 ? '' : 's'} haven’t uploaded yet.</b> Removing the offline copy now will permanently lose that work. Sync first, or remove anyway below.
              </Alert>
            ) : (
              <Box sx={{ fontSize: 12.5, color: 'var(--muted)' }}>
                Remove the offline copy once you’ve finished and synced this visit, to free up space. Already-uploaded work is kept on the server.
              </Box>
            )}
            {error && <Alert severity="error" sx={{ mt: 1.5, '& .MuiAlert-message': { fontSize: 12.5 } }}>{error}</Alert>}
          </Box>
        )}

        {stage === 'warn' && (
          <Box>
            <Box sx={{ fontSize: 13.5, color: 'var(--text)', mb: 1.5 }}>
              This downloads <b>{assetCount} asset{assetCount === 1 ? '' : 's'}</b>{visit && visit.company ? <> for <b>{visit.company}</b></> : null} so you can inspect them with no signal on site.
            </Box>

            <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 1.5, '& .MuiAlert-message': { fontSize: 12.5 } }}>
              <b>Your offline inspections are saved only on this device until you sync.</b> If you
              log out, remove or uninstall the app, clear your browser data, or lose this device
              <b> before syncing back online</b>, that work will be <b>permanently lost</b> and cannot be recovered.
            </Alert>

            {(free != null || storage.usage != null) && (
              <Box sx={{ fontSize: 11.5, color: 'var(--muted)', mb: 1 }}>
                {free != null ? <>Estimated free space on device: <b>{fmtMB(free)}</b>.</> : null}
              </Box>
            )}

            {!isStandalone() && (
              <Box sx={{ fontSize: 11.5, color: 'var(--muted)', mb: 1 }}>
                Tip: install AIMS to your home screen for the most reliable offline storage - a plain browser tab is more likely to clear saved data.
              </Box>
            )}

            <FormControlLabel
              sx={{ alignItems: 'flex-start', mt: 0.5 }}
              control={<Checkbox checked={ack} onChange={(e) => setAck(e.target.checked)} sx={{ pt: 0.25 }} />}
              label={<Box sx={{ fontSize: 12.5, color: 'var(--text)' }}>
                I understand my inspections will be lost if I don't sync before logging out, uninstalling, or clearing data.
              </Box>}
            />
          </Box>
        )}

        {stage === 'work' && (
          <Box sx={{ py: 1 }}>
            <Box sx={{ fontSize: 13.5, color: 'var(--text)', mb: 1 }}>
              {progress ? (PHASE_LABEL[progress.phase] || 'Working…') : 'Working…'}
            </Box>
            <LinearProgress
              variant={progress && progress.total > 1 ? 'determinate' : 'indeterminate'}
              value={progress && progress.total > 1 ? (progress.done / progress.total) * 100 : undefined}
              sx={{ height: 8, borderRadius: 4 }}
            />
            {progress && progress.total > 1 && (
              <Box sx={{ fontSize: 11.5, color: 'var(--muted)', mt: 0.75 }}>{progress.done} of {progress.total}</Box>
            )}
            <Box sx={{ fontSize: 11.5, color: 'var(--muted)', mt: 1 }}>Keep the app open until this finishes.</Box>
          </Box>
        )}

        {stage === 'done' && (
          <Box sx={{ py: 1 }}>
            {error ? (
              <Alert severity="error" sx={{ '& .MuiAlert-message': { fontSize: 12.5 } }}>{error}</Alert>
            ) : (
              <Box sx={{ textAlign: 'center', py: 1 }}>
                <CheckCircleIcon sx={{ fontSize: 46, color: 'var(--pass, #2e7d32)' }} />
                <Box sx={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', mt: 1 }}>Everything cached ✓</Box>
                <Box sx={{ fontSize: 12.5, color: 'var(--muted)', mt: 0.5 }}>
                  {result ? <>{result.cachedAssets} asset{result.cachedAssets === 1 ? '' : 's'} and {result.cachedTemplates} checklist{result.cachedTemplates === 1 ? '' : 's'} saved. </> : null}
                  You can now inspect this visit with no signal.
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        {stage === 'manage' && (
          <>
            <Button
              color="error"
              disabled={removing}
              onClick={() => remove(removeBlocked > 0)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {removing ? 'Removing…' : removeBlocked > 0 ? 'Remove anyway' : 'Remove offline copy'}
            </Button>
            <Button variant="contained" onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600 }}>Done</Button>
          </>
        )}
        {stage === 'warn' && (
          <>
            <Button onClick={onClose} sx={{ textTransform: 'none', color: 'var(--muted)' }}>Cancel</Button>
            <Button variant="contained" disabled={!ack} onClick={run} sx={{ textTransform: 'none', fontWeight: 600 }}>
              Confirm &amp; download
            </Button>
          </>
        )}
        {stage === 'work' && (
          <Button disabled sx={{ ml: 'auto', textTransform: 'none' }}>Working…</Button>
        )}
        {stage === 'done' && (
          <>
            {error ? <Button onClick={() => setStage('warn')} sx={{ textTransform: 'none' }}>Retry</Button> : <span />}
            <Button variant="contained" onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600 }}>Done</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
